import { Injectable } from '@angular/core';
import { BaseFacilityService } from './base-facility.service';
import { CommunicationService } from '../../../services/communication.service';
import { UrlSegments } from '../../jurisdiction.constants';
import {
  BaseDataObject,
  HeatingDeviceObject,
  HeatingFacilityData,
  HeatingFacilityResponse,
  HeatingMeterData,
  MeterDataObject,
  OldMeterData,
} from '../../facilities-form/facilities-form.domain';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EnvironmentService } from '../../../services/environment.service';

@Injectable({
  providedIn: 'root',
})
export class HeatingFacilityService extends BaseFacilityService {
  constructor(communicationService: CommunicationService, private environmentService: EnvironmentService) {
    super(communicationService, {
      UpdateInstallationUrl: UrlSegments.UpdateHeatingInstallation,
      UpdateFacilityUrl: UrlSegments.UpdateHeatingFacility,
      UpdateMeterUrl: UrlSegments.UpdateHeatingMeter,
      CreateMeterUrl: UrlSegments.CreateHeatingMeter,
      UpdateOldMeterUrl: UrlSegments.UpdateOldHeatingMeter,
      CreateOldMeterUrl: UrlSegments.CreateOldHeatingMeter,
    });
  }

  protected get hasDevices(): boolean {
    return false;
  }

  protected createFacility(request: any): Observable<string> {
    return this.communicationService
      .postData<HeatingFacilityResponse>(UrlSegments.CreateHeatingFacility, request)
      .pipe(map(response => response.districtheatfacility.find(() => true).facility_id));
  }

  protected mapToFacilityRequest(facility: BaseDataObject): HeatingFacilityData {
    let heatingDevice: HeatingDeviceObject = {} as HeatingDeviceObject;
    if (facility.heatingDevice) {
      heatingDevice = facility.heatingDevice.customFields
        ? ({ ...facility.heatingDevice.customFields } as HeatingDeviceObject)
        : facility.heatingDevice;
    }

    return {
      design_temperature_tr: heatingDevice.designTempTr?.toString().replace(',', '.'),
      design_temperature_tv: heatingDevice.designTempTv?.toString().replace(',', '.'),
      drinking_water: heatingDevice.dhw ? '1' : '0',
      drinking_water_brand: heatingDevice.dhw ? heatingDevice.dhwType : '',
      drinking_water_volumen: heatingDevice.dhw ? heatingDevice.dhwvolume : '0',
      floor_design_temperature_tr: heatingDevice.designTempFloorHeatingTr,
      floor_design_temperature_tv: heatingDevice.designTempFloorHeatingTv,
      floor_fowrate: heatingDevice.flowFloorHeating,
      floor_heating_power: heatingDevice.floorHeating,
      heating_load: heatingDevice.heatingLoad?.toString().replace(',', '.'),
      heating_power: heatingDevice.installHeatPower?.toString().replace(',', '.'),
      radiator_deviation: heatingDevice.deviation,
      radiator_flowrate: heatingDevice.flowRadiators,
      radiator_thermovalve_brand: heatingDevice.thermoValveType,
      swkn_districtheat_device_location_text: heatingDevice.installDirections,
      sk_swkn_districthead_system_type: heatingDevice.dhwType,
      sk_districtheat_device_location: facility.placeOfMeter,
      sk_districtheat_distribution_type: heatingDevice.dhType || '',
      sk_districtheat_heating_type: heatingDevice.dhTypeHeating || '',
      sk_districtheat_facility_type: facility.facilityType || '',
      transaction_no: facility.noOfConnections,
      commercial_sector: facility.sector,
      other_location_details: facility.otherLocation,
      reason_for_request: facility.reasonForRequest,
      heated_living_space: facility.heatedLivingSpace,
      volume_flow_qmax: facility.volumeFlowQmax,
      flow_temperature: facility.flowTemperature,
      return_temperature: facility.returnTemperature,
    };
  }

  protected mapToMeterRequest(data: MeterDataObject): HeatingMeterData {
    return {
      consumption_forecast: data.annualConsumptionForecast,
      meter_count: data.amount,
      sk_communication: data.selectComm,
      sk_districtheat_meter_type: data.meterType,
      ...(this.environmentService.showSkMeterMountHeat && { sk_meter_mount: data.selectMount }),
      sk_heat_meter_size: data.counterSize,
      sk_mounting_alignment: data.alignment,
      requested_total_power: data.requestedTotalPower,
      ...(this.environmentService.showOldMeterField && { old_meter_number: data.oldMeterNumber }),
    };
  }

  protected mapToOldMeterRequest(data: MeterDataObject): OldMeterData {
    return {
      replaced_meter_no: data.meterNumber,
      replaced_meter_reading: data.meterReading,
      replaced_meter_image_id: data.meterImageId,
      replaced_meter_action: data.meterAction,
    };
  }
}
